// src/utils/estimateApiClient.js


function buildUrl(path) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  if (!base) return path; // same-origin
  return `${base}${path}`;
}

/* ---------------- reCAPTCHA helpers (v3 + v2 Invisible) ---------------- */

const V3_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3 || "").trim();
const V2_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2 || "").trim();

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/** Ensure grecaptcha v3 is ready (renderless) */
async function ensureV3() {
  if (!V3_KEY) throw new Error("Missing REACT_APP_RECAPTCHA_SITE_KEY_V3");
  await loadScriptOnce(`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(V3_KEY)}`);
  await new Promise((r) => window.grecaptcha.ready(r));
}

/** Get a v3 token for an action */
async function getV3Token(action) {
  await ensureV3();
  return window.grecaptcha.execute(V3_KEY, { action });
}

/** Ensure a single hidden v2 invisible widget exists; return its widgetId */
let v2WidgetId = null;
async function ensureV2Invisible() {
  if (!V2_KEY) throw new Error("Missing REACT_APP_RECAPTCHA_SITE_KEY_V2");
  // Load v2 API if not present
  if (typeof window.grecaptcha === "undefined" || !window.grecaptcha.render) {
    await loadScriptOnce("https://www.google.com/recaptcha/api.js");
  }
  // Render once into a hidden container
  if (v2WidgetId === null) {
    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.bottom = "0";
    host.style.right = "0";
    host.style.width = "1px";
    host.style.height = "1px";
    host.style.overflow = "hidden";
    host.style.opacity = "0";
    document.body.appendChild(host);

    v2WidgetId = window.grecaptcha.render(host, {
      sitekey: V2_KEY,
      size: "invisible",
      badge: "inline", // hidden container anyway
      callback: () => {}, // we’ll use the Promise below instead
    });
  }
  return v2WidgetId;
}

/** Run the invisible v2 challenge and resolve with a token */
async function getV2Token() {
  const id = await ensureV2Invisible();
  return new Promise((resolve, reject) => {
    try {
      window.grecaptcha.execute(id);
      // grecaptcha.getResponse(id) returns the token after execute completes;
      // poll briefly to pick it up, then reset for next time.
      const started = Date.now();
      const poll = () => {
        const tok = window.grecaptcha.getResponse(id);
        if (tok) {
          // reset so future calls can re-execute
          window.grecaptcha.reset(id);
          resolve(tok);
        } else if (Date.now() - started > 20000) {
          reject(new Error("reCAPTCHA v2 timeout"));
        } else {
          requestAnimationFrame(poll);
        }
      };
      poll();
    } catch (e) {
      reject(e);
    }
  });
}

/* ---------------- Estimate API client ---------------- */

/**
 * POST /estimate/generate
 * Body:
 *  {
 *    payload: <23-field object>,
 *    recaptcha: { kind: "v3"|"v2", token: string, action: "cost_estimate" },
 *    locale?: string,
 *    client_nonce?: string
 *  }
 * Success: JSON estimate object with fee_range, etc.
 * Soft-step: { ok:false, needs_v2:true, recaptcha:{...} }  // HTTP 200
 * Error: { ok:false, error:"...", recaptcha?:{...} }       // 4xx/5xx
 */
export async function generateCaseEstimate(payload, opts = {}) {
  const url = buildUrl("/estimate/generate");
  const action = "cost_estimate";

  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? 15000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // 1) Get v3 token and attempt
  const v3Token = await getV3Token(action);

  let res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    signal: controller.signal,
    body: JSON.stringify({
      payload,
      recaptcha: { kind: "v3", token: v3Token, action },
      locale: navigator.language || "en-US",
      client_nonce:
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        String(Date.now()),
      ...opts.extra,
    }),
  });

  // 2) Parse response (may be success OR soft-step)
  let data;
  try {
    data = await res.json();
  } catch {
    clearTimeout(timer);
    throw new Error(`Estimate API returned non-JSON (status ${res.status}).`);
  }

  // Soft step (v2 needed) comes back as 200 + needs_v2:true
  if (res.ok && data && data.needs_v2 === true) {
    // 3) Run v2 Invisible and retry once
    const v2Token = await getV2Token();
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: controller.signal,
      body: JSON.stringify({
        payload,
        recaptcha: { kind: "v2", token: v2Token, action },
        locale: navigator.language || "en-US",
        client_nonce:
          (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
          String(Date.now()),
        ...opts.extra,
      }),
    });

    try {
      data = await res.json();
    } catch {
      clearTimeout(timer);
      throw new Error(`Estimate API (v2 retry) returned non-JSON (status ${res.status}).`);
    }
  }

  clearTimeout(timer);

  if (!res.ok) {
    const reason = data?.error || data?.recaptcha?.reason || `HTTP_${res.status}`;
    throw new Error(`Estimate request failed: ${reason}`);
  }

  if (!data || typeof data !== "object" || !data.fee_range) {
    // If backend ever changes, surface diagnostic
    throw new Error("Estimate API returned unexpected payload.");
  }

  return data;
}
